import { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import PublishIcon from '@mui/icons-material/Publish';
import { SeoTask } from '@/types/api/seo';

type Props = {
  tasks: SeoTask[];
  isLoading: boolean;
  onStatusChange: (taskId: string, status: SeoTask['status']) => Promise<void>;
  onApplyToWp: (task: SeoTask) => Promise<void>;
  wpConnected: boolean;
};

const STATUS_ORDER: Record<SeoTask['status'], number> = {
  todo: 0,
  in_progress: 1,
  done: 2,
  dismissed: 3,
};

const typeColors: Record<SeoTask['taskType'], string> = {
  title_tag: '#1a73e8',
  meta_description: '#0D9D58',
  content: '#A142F4',
  schema: '#F4B400',
  internal_link: '#4285F4',
  technical: '#EA4335',
  backlink: '#054AD9',
  other: '#96979b',
};

const typeLabels: Record<SeoTask['taskType'], string> = {
  title_tag: 'Title Tag',
  meta_description: 'Meta Desc',
  content: 'Content',
  schema: 'Schema',
  internal_link: 'Internal Link',
  technical: 'Technical',
  backlink: 'Backlink',
  other: 'Other',
};

const priorityColors: Record<SeoTask['priority'], string> = {
  high: '#EA4335',
  medium: '#F4B400',
  low: '#96979b',
};

const statusColors: Record<SeoTask['status'], string> = {
  todo: '#4285F4',
  in_progress: '#F4B400',
  done: '#0D9D58',
  dismissed: '#96979b',
};

const statusLabels: Record<SeoTask['status'], string> = {
  todo: 'To Do',
  in_progress: 'In Progress',
  done: 'Done',
  dismissed: 'Dismissed',
};

const WP_APPLICABLE_TYPES: SeoTask['taskType'][] = ['title_tag', 'meta_description', 'content', 'schema'];

const sectionSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 3,
  p: 3,
  backgroundColor: '#fff',
  mb: 4,
};

const KeywordTaskListComponent = function ({
  tasks,
  isLoading,
  onStatusChange,
  onApplyToWp,
  wpConnected,
}: Props): JSX.Element | null {
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  const sortedTasks = useMemo(() => {
    return [...tasks].sort((a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status]);
  }, [tasks]);

  const handleStatusChange = useCallback(async (taskId: string, status: SeoTask['status']) => {
    setUpdatingId(taskId);
    try {
      await onStatusChange(taskId, status);
    } finally {
      setUpdatingId(null);
    }
  }, [onStatusChange]);

  const handleApplyToWp = useCallback(async (task: SeoTask) => {
    setUpdatingId(task.id);
    try {
      await onApplyToWp(task);
    } finally {
      setUpdatingId(null);
    }
  }, [onApplyToWp]);

  if (isLoading) {
    return (
      <Box sx={sectionSx}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <AssignmentOutlinedIcon sx={{ fontSize: 20, color: '#054AD9' }} />
          <Typography variant="overline" fontWeight={700} color="text.secondary" sx={{ letterSpacing: 1.5 }}>
            Tasks
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} sx={{ color: '#054AD9' }} />
        </Box>
      </Box>
    );
  }

  if (tasks.length === 0) {
    return (
      <Box sx={sectionSx}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <AssignmentOutlinedIcon sx={{ fontSize: 20, color: '#054AD9' }} />
          <Typography variant="overline" fontWeight={700} color="text.secondary" sx={{ letterSpacing: 1.5 }}>
            Tasks
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          No tasks yet. Generate AI suggestions in the Ranking Playbook above, then click "Add Task" to create tasks.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={sectionSx}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <AssignmentOutlinedIcon sx={{ fontSize: 20, color: '#054AD9' }} />
        <Typography variant="overline" fontWeight={700} color="text.secondary" sx={{ letterSpacing: 1.5 }}>
          Tasks
        </Typography>
        <Chip label={`${tasks.length} task${tasks.length !== 1 ? 's' : ''}`} size="small" sx={{ fontSize: 10, height: 20 }} />
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        Action items generated from opportunities and AI analysis for this keyword.
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {sortedTasks.map((task) => {
          const isUpdating = updatingId === task.id;
          const canApplyToWp = wpConnected && !task.appliedToWp && task.status !== 'done' && task.suggestedValue && WP_APPLICABLE_TYPES.includes(task.taskType);

          return (
            <Box
              key={task.id}
              sx={{
                p: 2,
                borderRadius: 2,
                border: '1px solid',
                borderColor: task.status === 'done' ? '#0D9D5830' : 'divider',
                backgroundColor: task.status === 'done' ? '#f6fdf8' : '#fafafa',
                opacity: task.status === 'done' ? 0.8 : 1,
              }}
            >
              {/* Title row */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                <Typography variant="body2" component="div" fontWeight={600} sx={{ fontSize: 13, flex: 1 }}>
                  {task.title}
                </Typography>
                <Chip
                  label={typeLabels[task.taskType]}
                  size="small"
                  sx={{
                    fontSize: 9,
                    height: 18,
                    fontWeight: 600,
                    color: '#fff',
                    backgroundColor: typeColors[task.taskType],
                  }}
                />
                <Chip
                  label={task.priority}
                  size="small"
                  sx={{
                    fontSize: 9,
                    height: 18,
                    fontWeight: 600,
                    color: '#fff',
                    backgroundColor: priorityColors[task.priority],
                    textTransform: 'capitalize',
                  }}
                />
                <Chip
                  label={statusLabels[task.status]}
                  size="small"
                  variant="outlined"
                  sx={{
                    fontSize: 9,
                    height: 18,
                    fontWeight: 600,
                    borderColor: statusColors[task.status],
                    color: statusColors[task.status],
                  }}
                />
              </Box>

              {/* Suggested value preview */}
              {task.suggestedValue && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: 'block',
                    mb: 1,
                    fontSize: 11,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '100%',
                    backgroundColor: '#f0f7ff',
                    borderRadius: 1,
                    px: 1,
                    py: 0.5,
                  }}
                  title={task.suggestedValue}
                >
                  Suggested: {task.suggestedValue}
                </Typography>
              )}

              {/* Actions */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {isUpdating ? (
                  <CircularProgress size={16} sx={{ color: '#054AD9' }} />
                ) : (
                  <>
                    {task.status === 'todo' && (
                      <Tooltip title="Mark In Progress">
                        <IconButton size="small" onClick={() => handleStatusChange(task.id, 'in_progress')} sx={{ p: 0.5 }}>
                          <PlayArrowIcon sx={{ fontSize: 16, color: '#F4B400' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    {(task.status === 'todo' || task.status === 'in_progress') && (
                      <Tooltip title="Mark Done">
                        <IconButton size="small" onClick={() => handleStatusChange(task.id, 'done')} sx={{ p: 0.5 }}>
                          <CheckCircleOutlineIcon sx={{ fontSize: 16, color: '#0D9D58' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    {task.status !== 'dismissed' && task.status !== 'done' && (
                      <Tooltip title="Dismiss">
                        <IconButton size="small" onClick={() => handleStatusChange(task.id, 'dismissed')} sx={{ p: 0.5 }}>
                          <CloseIcon sx={{ fontSize: 16, color: '#96979b' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canApplyToWp && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<PublishIcon sx={{ fontSize: 14 }} />}
                        onClick={() => handleApplyToWp(task)}
                        sx={{
                          ml: 'auto',
                          fontSize: 10,
                          textTransform: 'none',
                          borderColor: '#21759B',
                          color: '#21759B',
                          py: 0.25,
                          px: 1,
                          minHeight: 24,
                          '&:hover': { borderColor: '#1a5f7a', backgroundColor: '#21759B10' },
                        }}
                      >
                        Apply to WP
                      </Button>
                    )}
                    {task.appliedToWp && (
                      <Chip
                        label="Applied to WP"
                        size="small"
                        sx={{ ml: 'auto', fontSize: 9, height: 18, color: '#21759B', backgroundColor: '#21759B15' }}
                      />
                    )}
                  </>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default KeywordTaskListComponent;
